import Swiper from '@/components/Common/Swiper/Swiper';
import { RaffleDealStoreCtx } from '@/stores/RaffleDealStore';
import type { ReactIdSwiperProps } from '@yanolja-rnd/react-swiper';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import { propEq, times } from 'ramda';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import RaffleDealExpectedProductUnit from '../Unit/RaffleDealExpectedProductUnit';
import RaffleDealProductUnit from '../Unit/RaffleDealProductUnit';
import RaffleDealBanner from './RaffleDealBanner';
import styles from './RaffleDealDrawBody.module.scss';
import RaffleDealNotice from './RaffleDealNotice';
import RaffleDealPrepare from './RaffleDealPrepare';
import useCountDown from './useCountDown';

const swiperConfig: ReactIdSwiperProps = {
  slidesPerView: 'auto',
  spaceBetween: 10,
};

const RaffleDealDrawBody: React.FC = () => {
  const store = useContext(RaffleDealStoreCtx);

  const countDownRef = useRef(null);
  const [hours, setHours] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [seconds, setSeconds] = useState('00');

  const {
    promotion: { scheduledDealExposure, scheduledDealExposureCount },
    runningDeals,
    scheduledDeals,
    images,
  } = store.PromotionData;

  // 예정 상품 swiper 노출 갯수
  const expectedDealsCount = useMemo(() => {
    return scheduledDeals.length < scheduledDealExposureCount ? scheduledDeals.length + 1 : scheduledDealExposureCount;
  }, [scheduledDeals]);

  // 키 비쥬얼
  const topImg = useMemo(() => images.filter(propEq('section', 'TOP'))[0], [images]);

  // 중간 이미지
  const middleImg = useMemo(() => images.filter(propEq('section', 'MIDDLE')).sort()[0], [images]);

  // 배너
  const banners = useMemo(() => images.filter(propEq('section', 'BANNER')).sort(), [images]);

  const startDate = moment(runningDeals?.[0]?.startAt);
  const endDate = moment(runningDeals?.[0]?.endAt);

  // 카운트다운 타이머
  const countDown = useCallback((): void => {
    const diffTime = moment(endDate).diff(store.now, 'milliseconds');
    let duration = moment.duration(diffTime, 'milliseconds');

    if (!countDownRef.current || Number(duration) - 1000 < 0) {
      return;
    }

    duration = moment.duration(Number(duration) - 1000, 'milliseconds');
    store.setNow(store.now.clone().add(1000, 'milliseconds'));

    setHours(moment(duration.hours(), 'h').format('HH'));
    setMinutes(moment(duration.minutes(), 'm').format('mm'));
    setSeconds(moment(duration.seconds(), 's').format('ss'));

    // 남은 응모시간이 00:00:00 인 경우 페이지 새로고침해 다음 드로우 상품이 노출되게끔 한다.
    if (duration.hours() === 0 && duration.minutes() === 0 && duration.seconds() === 0) {
      window.location.reload();
    }
  }, []);

  const { startCountDown, clearCountDown } = useCountDown();

  // 현재 응모 가능한 상품이 있는지 여부
  const isInDrawPeriod = useMemo(() => store.now <= endDate && store.now >= startDate, [store.now, endDate, startDate]);

  useEffect(() => {
    if (isInDrawPeriod) {
      startCountDown(countDown);
    } else {
      clearCountDown();
    }

    // 브라우저가 백그라운드 상태인 경우 메모리 이슈로 setInterval이 정상 동작하지 않는다.
    // 화면이 다시 보여질 때 페이지 새로고침해 타이머가 새로 동작되도록 한다.
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        window.location.reload();
      } else {
        clearCountDown();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <section ref={countDownRef}>
      {/* 키 비주얼 */}
      <div className={styles.visual}>
        <img src={topImg?.url} alt="NOL 드로우" />
      </div>

      {isInDrawPeriod ? (
        <>
          {/* 카운트다운 타이머 */}
          <div className={styles.countdownContainer}>
            <div className={styles.countdownWrap}>
              <p className={styles.countdownTitle}>남은 응모시간</p>
              <div className={styles.timerWrap}>
                <span className={styles.time}>{hours}</span>
                <span className={styles.colon}>:</span>
                <span className={styles.time}>{minutes}</span>
                <span className={styles.colon}>:</span>
                <span className={styles.time}>{seconds}</span>
              </div>
            </div>
          </div>
          {/* 오늘의 상품 */}
          <div className={clsx(styles.drawWrap, styles.pb0)}>
            <h2 className={styles.bodyHeader}>오늘의 {store.PromotionTitle} 상품</h2>
            {runningDeals.map((deal) => (
              <RaffleDealProductUnit deal={deal} key={deal.id} />
            ))}
          </div>
        </>
      ) : (
        <RaffleDealPrepare />
      )}

      {/* 예정 상품 */}
      {scheduledDealExposure && (
        <div className={styles.drawWrap}>
          <h2 className={styles.bodyHeader}>{store.PromotionTitle} 예정 상품</h2>
          <Swiper className={styles.swiper} {...swiperConfig}>
            {times((n) => {
              return <RaffleDealExpectedProductUnit deal={scheduledDeals[n]} key={n} />;
            }, expectedDealsCount)}
          </Swiper>
        </div>
      )}

      {/* 드로우 설명 중간 이미지 */}
      <img className={styles.middleImg} src={middleImg?.url} alt={middleImg?.altText} />

      {/* 혜택 배너 */}
      {banners && banners.length > 0 && <RaffleDealBanner banners={banners} />}

      {/* 유의사항 */}
      <RaffleDealNotice />
    </section>
  );
};

export default observer(RaffleDealDrawBody);
